import { sendRequest, uniquenessCheck, API_ROOT } from './requests';
import { addPopup, addNotification } from './store';
import { ConfirmationDialogue } from '../general_components/forms/ConfirmationDialogue';
import { setAndRemoveError } from '../general_components/forms/input_fields/InputFieldEventHandlers';

const LOOKUP_TIMEOUT_MS = 25000;
const FALLBACK_PROVIDERS = ['Samsara', 'Motive'];

const ENTITIES = {
    trucks: {
        noun: 'truck',
        relatedCategory: 'drivers',
        existsMessage: 'Truck with this Tracking ID already exists',
        linkedLabel: (label) => `truck ${label}`,
        relatedMessage: (provider, related) => related.length === 1
            ? `${provider} ID of driver ${related[0].label} was also found. Should we assign it as well?`
            : `${provider} IDs of drivers ${related.map(r => r.label).join(' and ')} were also found. Should we assign them as well?`,
    },
    drivers: {
        noun: 'driver',
        relatedCategory: 'trucks',
        existsMessage: 'Driver with this Tracking ID already exists',
        linkedLabel: (label) => label,
        relatedMessage: (provider, related) =>
            `${provider} ID of truck ${related.map(r => r.label).join(', ')} was also found. Should we assign it as well?`,
    },
};

const sameText = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

/**
 * One per edit popup: holds changes to other records (transfer away from another
 * record, IDs for the related truck/drivers) that are only applied once the form saves.
 */
export const createTrackingSession = () => ({ lookedUp: null, transfer: null, related: [], cancelLookup: null });

const resetSession = (session) => {
    if (session.cancelLookup) session.cancelLookup();
    session.lookedUp = null;
    session.transfer = null;
    session.related = [];
};

/** Tracking Provider select + tracking ID input for a truck/driver Overview edit form. */
export const trackingColumns = (owner, category, session = createTrackingSession()) => {
    const data = owner.data;
    const options = Array.isArray(data.trackingProviderOptions) ? [...data.trackingProviderOptions] : [...FALLBACK_PROVIDERS];
    if (data.trackingProvider && !options.some(p => sameText(p, data.trackingProvider))) {
        options.push(data.trackingProvider);
    }
    return [
        {
            notInAddForm: true,
            label: 'Tracking Provider',
            name: 'trackingProvider',
            val: data.trackingProvider || '-',
            type: 'select',
            dataset: ['', ...options],
            processBeforeSending: val => val === '' ? null : val,
            defaultValue: () => data.trackingProvider || '',
            onChange: event => onProviderChange(event, owner, category, session),
        },
        {
            notInAddForm: true,
            label: data.trackingProvider ? `${data.trackingProvider} ID` : 'Tracking ID',
            val: data.trackingId,
            name: 'trackingId',
            defaultValue: () => data.trackingId,
            processBeforeSending: val => val === '' ? null : val,
            customDisplayMode: data.trackingProvider ? 'block' : 'none',
            spaceAllowed: false,
            hasConstraints: true,
            checks: [
                (value, column, target) => trackingIdUniqueness(value, target, owner, category, session),
            ],
        },
    ];
};

const trackingIdUniqueness = async (value, target, owner, category, session) => {
    if (!value) return null;
    const provider = target?.form?.elements?.trackingProvider?.value || null;
    const data = owner.data;
    if (sameText(provider, data.trackingProvider) && sameText(value, data.trackingId)) return null;
    const transfer = session.transfer;
    if (transfer && sameText(provider, transfer.provider) && sameText(value, transfer.trackingId)) return null;
    return uniquenessCheck(
        `${API_ROOT}${category}/exists`, 'trackingId', value, ENTITIES[category].existsMessage, 'tracking ID',
        { provider, excludeId: data.id },
    );
};

const onProviderChange = (event, owner, category, session) => {
    const select = event.target;
    const form = select.form;
    if (!form) return;
    const provider = select.value;
    const idInput = form.querySelector('input[name="trackingId"]');
    const idLabel = form.querySelector('#trackingIdLabel');
    if (!idInput) return;

    resetSession(session);
    idInput.disabled = !provider;
    if (provider) {
        idInput.parentNode.style.display = 'block';
        if (idLabel) idLabel.textContent = `${provider} ID`;
    } else {
        idInput.value = '';
        idInput.parentNode.style.display = 'none';
    }
    setAndRemoveError({ name: 'trackingId' }, idInput, null, false);
    if (!provider) return;

    const data = owner.data;
    if (sameText(provider, data.trackingProvider) && data.trackingId) {
        idInput.value = data.trackingId;
        return;
    }
    idInput.value = '';
    runLookup({ form, idInput, idLabel, provider, owner, category, session });
};

/** Disables every control in the form (and its footer submit) until the returned unlock is called. */
const lockForm = (form, idLabel) => {
    const submit = document.querySelector(`input[type="submit"][form="${form.id}"]`);
    const controls = [...form.querySelectorAll('input, select, textarea, button')];
    if (submit) controls.push(submit);
    const previous = controls.map(el => [el, el.disabled]);
    controls.forEach(el => { el.disabled = true; });
    submit?.parentElement?.classList.add('disabled');
    idLabel?.setAttribute('data-loading', 'true');
    return () => {
        previous.forEach(([el, wasDisabled]) => { el.disabled = wasDisabled; });
        submit?.parentElement?.classList.remove('disabled');
        idLabel?.setAttribute('data-loading', 'false');
    };
};

const runLookup = (ctx) => {
    const { form, idLabel, provider, owner, category, session } = ctx;
    const unlock = lockForm(form, idLabel);
    const controller = new AbortController();
    let settled = false;
    const settle = () => {
        if (settled) return false;
        settled = true;
        clearTimeout(timer);
        session.cancelLookup = null;
        unlock();
        return form.isConnected;
    };
    const timer = setTimeout(() => {
        controller.abort();
        if (settle()) {
            addNotification({ message: `${provider} did not respond in time. Enter the ID manually.`, type: 'alert' });
        }
    }, LOOKUP_TIMEOUT_MS);
    session.cancelLookup = () => {
        controller.abort();
        settle();
    };

    sendRequest('GET', `${API_ROOT}${category}/${owner.data.id}/tracking-lookup?provider=${provider}`, controller.signal,
        (result) => {
            if (settle()) handleLookupResult(ctx, result);
        },
        (error) => {
            if (settle()) {
                addNotification({ message: error?.customError || `Could not look up the ${provider} ID. Enter it manually.`, type: 'alert' });
            }
        },
    );
};

const handleLookupResult = (ctx, result) => {
    const { provider } = ctx;
    switch (result?.status) {
        case 'MATCHED':
            if (result.linkedTo) {
                confirmTransfer(ctx, result);
            } else {
                fillTrackingId(ctx, result);
                addNotification({ message: `${provider} ID found${result.matchedName ? ` (${result.matchedName})` : ''}`, type: 'success' });
                offerRelatedAssignments(ctx, result);
            }
            break;
        case 'NO_MATCH':
            addNotification({ message: `No unique ${provider} match found. Enter the ID manually.`, type: 'notify' });
            break;
        default:
            addNotification({ message: result?.message || `Could not look up the ${provider} ID. Enter it manually.`, type: 'alert' });
    }
};

const fillTrackingId = (ctx, result) => {
    ctx.idInput.value = result.trackingId;
    setAndRemoveError({ name: 'trackingId' }, ctx.idInput, null, false);
    ctx.session.lookedUp = { provider: ctx.provider, trackingId: result.trackingId };
};

const confirmTransfer = (ctx, result) => {
    const { provider, category, session, form } = ctx;
    const entity = ENTITIES[category];
    const matched = result.matchedName ? ` (${result.matchedName})` : '';
    addPopup({
        content: ConfirmationDialogue,
        popupData: {
            confirmationDialogue: true,
            name: 'TrackingIdTransfer',
            formTitle: 'Tracking ID In Use',
            message: `${provider} ID ${result.trackingId}${matched} is already assigned to ${entity.linkedLabel(result.linkedTo.label)}. Transfer it to this ${entity.noun}? It will be removed from ${entity.linkedLabel(result.linkedTo.label)} when you save.`,
            submitTitle: 'Transfer',
            cancelButtonText: 'No',
            action: (postResultAction) => {
                postResultAction(true);
                if (!form.isConnected) return;
                fillTrackingId(ctx, result);
                session.transfer = { ...result.linkedTo, provider, trackingId: result.trackingId };
                offerRelatedAssignments(ctx, result);
            },
            cancelAction: (closePopup) => closePopup(),
        },
        postSuccessAction: () => {},
        EZCloseAllowed: false,
    });
};

const offerRelatedAssignments = (ctx, result) => {
    const { provider, category, session, form } = ctx;
    const related = result.relatedAssignments || [];
    if (related.length === 0) return;
    addPopup({
        content: ConfirmationDialogue,
        popupData: {
            confirmationDialogue: true,
            name: 'TrackingIdRelated',
            formTitle: 'Tracking IDs Found',
            message: `${ENTITIES[category].relatedMessage(provider, related)} They will be assigned when you save.`,
            submitTitle: 'Assign',
            cancelButtonText: 'No',
            action: (postResultAction) => {
                postResultAction(true);
                if (!form.isConnected) return;
                session.related = related.map(r => ({ ...r, provider }));
            },
            cancelAction: (closePopup) => closePopup(),
        },
        postSuccessAction: () => {},
        EZCloseAllowed: false,
    });
};

const patchTracking = (category, id, provider, trackingId, onSuccess) => {
    sendRequest('PATCH', `${API_ROOT}${category}/edit`, null, onSuccess,
        (e) => addNotification({ message: e?.customError || 'Could not update the tracking ID', type: 'alert' }),
        { id, trackingProvider: provider, trackingId },
    );
};

/** Applies staged transfer/related changes once the edit form saved the looked-up provider + ID. */
export const applyTrackingSessionAfterSave = (session, saved, category) => {
    if (!session || !saved || !session.lookedUp) return;
    const { provider, trackingId } = session.lookedUp;
    if (!sameText(saved.trackingProvider, provider) || !sameText(saved.trackingId, trackingId)) return;
    const entity = ENTITIES[category];

    const transfer = session.transfer;
    if (transfer) {
        patchTracking(category, transfer.id, null, null, () => addNotification({
            message: `${provider} ID removed from ${entity.linkedLabel(transfer.label)}`, type: 'success',
        }));
    }
    for (const assignment of session.related) {
        patchTracking(entity.relatedCategory, assignment.id, assignment.provider, assignment.trackingId, () => addNotification({
            message: `${assignment.provider} ID assigned to ${ENTITIES[entity.relatedCategory].linkedLabel(assignment.label)}`, type: 'success',
        }));
    }
    session.transfer = null;
    session.related = [];
};
